import {z} from '@hono/zod-openapi';

import {E164PhoneNumberSchema} from './E164PhoneNumberSchema';

export const INCOMING_VOICE_CALL_COMMAND_SCHEMA_VERSION = '1.1.0';

/**
 * Admission command for one inbound telephone call, published by the OpenAI Realtime SIP
 * ingress after it verifies the OpenAI webhook signature.
 *
 * Version 1.1.0 adds the DMS call-context correlation fields the ingress reads from the SIP
 * headers of the inbound INVITE:
 * - `callReference` from the custom `X-Omnetic-Call-Reference` header. DMS sets it when it
 *   dials OpenAI SIP, after resolving the tenant for the called line; the Gateway joins this
 *   command to `VoiceCallContextRegisteredEventSchema` by the same value. Absent when DMS did
 *   not set the header.
 * - `calledNumber` from the SIP `To` header, user part, in E.164.
 * - `callerNumber` from the SIP `From` header, user part, in E.164. Absent when the caller
 *   number is withheld.
 */
export const IncomingVoiceCallCommandSchema = z
  .object({
    schemaVersion: z.literal(INCOMING_VOICE_CALL_COMMAND_SCHEMA_VERSION),
    eventType: z.literal('realtime.call.incoming'),
    webhookId: z.string().min(1),
    openAiEventId: z.string().min(1),
    callId: z.string().min(1),
    receivedAt: z.string().datetime({offset: true}),
    sipCallId: z.string().min(1).optional(),
    callReference: z.string().uuid().optional(),
    calledNumber: E164PhoneNumberSchema.optional(),
    callerNumber: E164PhoneNumberSchema.optional(),
  })
  .strict()
  .openapi('IncomingVoiceCallCommandSchema');

export type IncomingVoiceCallCommand = z.infer<typeof IncomingVoiceCallCommandSchema>;
